/**
 * Earnings Catalyst — Trading UI client. Read-only.
 *
 * Reads /api/v1/earnings-catalysts/* over same-origin nginx proxy.
 * No order controls, no fabricated fields. Missing values → "(unavailable)".
 *
 * Marker strings (must appear in /etc/adversyn/trading-ui-markers.json):
 *   - "earnings-catalysts"
 *   - "phase-catalyst-ui-v1"
 */
import { apiV1Base } from "./apiConfig";

type Envelope<T> = {
  ok: boolean;
  data: T | null;
  error?: { message: string; code: string } | null;
};

export interface EarningsCalendarRow {
  event_id?: number | string | null;
  symbol?: string | null;
  company_name?: string | null;
  report_date?: string | null;
  fiscal_period?: string | null;
  report_time?: string | null;          // BMO|AMC|TBD
  eps_estimate?: number | null;
  revenue_estimate?: number | null;
  source?: string | null;
}

export interface EarningsWatchRow {
  watch_id?: number | string | null;
  event_id?: number | string | null;
  symbol?: string | null;
  status?: string | null;               // open|closed
  bias?: string | null;                 // BULLISH|BEARISH|NEUTRAL
  confidence?: number | null;
  pre_earnings_thesis?: string | null;
  event_report_date?: string | null;
  fiscal_period?: string | null;
  created_at_utc?: string | null;
}

export interface EarningsResultRow {
  result_id?: number | string | null;
  event_id?: number | string | null;
  symbol?: string | null;
  reported_eps?: number | null;
  reported_revenue?: number | null;
  eps_surprise_pct?: number | null;
  revenue_surprise_pct?: number | null;
  guidance_direction?: string | null;
  event_report_date?: string | null;
  fiscal_period?: string | null;
  created_at_utc?: string | null;
}

export interface EarningsReviewRow {
  review_id?: number | string | null;
  watch_id?: number | string | null;
  event_id?: number | string | null;
  symbol?: string | null;
  success_status?: string | null;       // matched|contradicted|inconclusive|unavailable_*
  watch_thesis?: string | null;
  notes?: string | null;
  event_report_date?: string | null;
  fiscal_period?: string | null;
  created_at_utc?: string | null;
}

export interface EarningsPayload<R> {
  rows: R[];
  row_count: number;
  filters: Record<string, unknown>;
  source_table: string;
}

async function ecGet<T>(path: string): Promise<Envelope<T>> {
  try {
    const res = await fetch(`${apiV1Base()}${path}`, {
      headers: { accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return {
        ok: false,
        data: null,
        error: { message: `HTTP ${res.status}`, code: String(res.status) },
      };
    }
    return (await res.json()) as Envelope<T>;
  } catch (err: any) {
    return {
      ok: false,
      data: null,
      error: { message: err?.message || "network", code: "NETWORK" },
    };
  }
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export const getEarningsCalendar = (start?: string, end?: string, symbol?: string, limit = 200) =>
  ecGet<EarningsPayload<EarningsCalendarRow>>(
    `/earnings-catalysts/calendar${qs({ start, end, symbol, limit })}`,
  );

export const getEarningsWatchlist = (status?: string, symbol?: string, limit = 200) =>
  ecGet<EarningsPayload<EarningsWatchRow>>(
    `/earnings-catalysts/watchlist${qs({ status, symbol, limit })}`,
  );

export const getEarningsResults = (start?: string, end?: string, symbol?: string, limit = 200) =>
  ecGet<EarningsPayload<EarningsResultRow>>(
    `/earnings-catalysts/results${qs({ start, end, symbol, limit })}`,
  );

export const getEarningsReviews = (symbol?: string, success_status?: string, limit = 200) =>
  ecGet<EarningsPayload<EarningsReviewRow>>(
    `/earnings-catalysts/reviews${qs({ symbol, success_status, limit })}`,
  );
